import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ExternalLink, Send, Star } from 'lucide-react';
export const TRUSTPILOT_REVIEW_URL = 'https://www.trustpilot.com/review/toxicdetector.com';
interface FeedbackFlowProps {
  /** Optional context line included in the private feedback email. */
  contextLabel?: string | null;
  /** Unique id prefix so the flow can render more than once per page. */
  idPrefix?: string;
  /** Called when the visitor leaves for Trustpilot. */
  onTrustpilotClick?: () => void;
}
/**
 * The rating → private feedback → public review flow.
 * Shared by the feedback modal and the landing "Share your story" section
 * so there is only ever one implementation of this experience.
 */
export function FeedbackFlow({
  contextLabel = null,
  idPrefix = 'feedback',
  onTrustpilotClick
}: FeedbackFlowProps) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [privateFeedbackSent, setPrivateFeedbackSent] = useState(false);
  const messageId = `${idPrefix}-message`;
  const handlePrivateFeedback = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const subject = encodeURIComponent('Private ToxicDetector feedback');
    const body = encodeURIComponent(`Rating: ${rating}/5\n${contextLabel ? `${contextLabel}\n` : ''}\n${feedbackText}`);
    window.location.href = `mailto:support@toxicdetector.com?subject=${subject}&body=${body}`;
    setPrivateFeedbackSent(true);
  };
  return <div data-id="element-1243" data-mp="2ndu4non8o353">
      <div data-id="element-1244" data-mp="2ndk97ljfd3s0">
        <p className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500" data-id="element-1245" data-mp="2ne2grebjf4f8">
          Step 1 — choose a rating
        </p>
        <div className="flex gap-2" role="radiogroup" aria-label="Rate your experience from one to five stars" data-id="element-1246" data-mp="2jebikpjolm0o">
          {[1, 2, 3, 4, 5].map(star => {
          const isSelected = star <= (hoverRating || rating);
          return <button key={star} type="button" role="radio" aria-checked={rating === star} aria-label={`${star} star${star === 1 ? '' : 's'}`} onClick={() => setRating(star)} onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)} className="rounded p-1 text-gray-700 transition-transform duration-150 ease-out hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#FF0000]" data-id="element-1247" data-mp="2rfyfxrjy071c">
                <Star size={32} strokeWidth={1.5} className={isSelected ? 'fill-[#FFD700] text-[#FFD700]' : 'text-gray-700'} data-id="element-1248" data-mp="2hqex2lw8cd6h" />
              </button>;
        })}
        </div>
      </div>

      {rating > 0 && <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.22,
      ease: [0.23, 1, 0.32, 1]
    }} className="mt-7 space-y-5 border-t border-gray-800 pt-6" data-id="element-1249" data-mp="1mhbdyjypb61e">
          <div data-id="element-1250" data-mp="18haacwr1xki8">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500" data-id="element-1251" data-mp="18jwhwwdy000g">
              Step 2 — optional private feedback
            </p>
            <p className="mt-2 text-sm leading-relaxed text-gray-400" data-id="element-1252" data-mp="yr79kadov29c">
              Tell us what worked or what we should improve. This opens a
              private message to our support team.
            </p>
          </div>

          <form onSubmit={handlePrivateFeedback} className="space-y-3" data-id="element-1253" data-mp="pp7rrfuunu5c">
            <label htmlFor={messageId} className="block text-sm font-bold text-white" data-id="element-1254" data-mp="kqwjh9k7drp8">
              Private feedback
            </label>
            <textarea id={messageId} value={feedbackText} onChange={event => setFeedbackText(event.target.value)} rows={4} placeholder="We’re listening — what would you like us to know?" className="w-full resize-none rounded-lg border border-gray-800 bg-[#0A0A0A] p-4 text-sm text-white outline-none transition-colors duration-150 ease-out placeholder:text-gray-600 focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000]" data-id="element-1255" data-mp="2fv4bygpd22yt" />
            <button type="submit" disabled={!feedbackText.trim()} className="inline-flex items-center gap-2 rounded text-sm font-bold text-gray-300 transition-colors duration-150 ease-out hover:text-white focus:outline-none focus:ring-2 focus:ring-white disabled:cursor-not-allowed disabled:text-gray-600" data-id="element-1256" data-mp="2179kf3n3a4nb">
              {privateFeedbackSent ? <CheckCircle2 size={16} aria-hidden="true" data-id="element-1257" data-mp="3mjaxyk0ld1en" /> : <Send size={16} aria-hidden="true" data-id="element-1258" data-mp="1axistzrdmsr5" />}
              {privateFeedbackSent ? 'Private feedback prepared' : 'Send private feedback'}
            </button>
          </form>

          <div className="rounded-lg border border-[#FFD700]/30 bg-[#FFD700]/5 p-4" data-id="element-1259" data-mp="1knlknwmri0nw">
            <p className="text-sm font-bold text-white" data-id="element-1260" data-mp="1ftzi4xabbd5t">
              Want to share publicly?
            </p>
            <p className="mt-1 text-sm leading-relaxed text-gray-400" data-id="element-1261" data-mp="1tl4arff9j56n">
              A Trustpilot review helps other people discover ToxicDetector and
              understand the experience before they try it.
            </p>
            <a href={TRUSTPILOT_REVIEW_URL} target="_blank" rel="noopener noreferrer" onClick={onTrustpilotClick} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#FF0000] px-5 py-3 text-sm font-black uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-[#D90000] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-1262" data-mp="1ym21igec97jr">
              Share on Trustpilot
              <ExternalLink size={16} aria-hidden="true" data-id="element-1263" data-mp="12w32kigaadmp" />
            </a>
          </div>
        </motion.div>}
    </div>;
}